import { StringHelper } from "@tylertech/shared-module";

import { LogLevel } from "../logLevel";
import { ILogger, Logger } from "./logger";

const debugAllParam = /debug=__ALL__/;
const debugSearchParam = /debug=(?:(\w+)[;,]?)*/i;
interface Window extends globalThis.Window
{
    // eslint-disable-next-line @typescript-eslint/naming-convention
    tt_loggerRegistry: LoggerRegistry;
}

declare const window: Window;

const storage = window.sessionStorage;

class LoggerRegistry
{
    protected readonly loggerCache = new Map<string, CustomLogger>();

    enabled(_loggerName: string): boolean
    {
        return true;
    }

    enable(_loggers: Array<string>): this;
    enable(_loggerName: string): this;
    enable(opt: Array<string> | string): this
    {
        /* no_op */
        return this;
    }

    disable(_loggers: Array<string>): this;
    disable(_loggerName: string): this;
    disable(opt: Array<string> | string): this
    {
        /* no_op */
        return this;
    }

    toggle(_loggers: Array<string>): this;
    toggle(_loggerName: string): this;
    toggle(opt: Array<string> | string): this
    {
        /* no_op */
        return this;
    }

    has(loggerName: string): boolean { return this.loggerCache.has(loggerName); }

    get(loggerName: string): CustomLogger { return this.loggerCache.get(loggerName); }

    set(loggerName: string, logger: CustomLogger): void
    {
        this.loggerCache.set(loggerName, logger);
    }

    forEach(fn: (loggerName: string) => void): void
    {
        this.loggerCache.forEach(
            (_, name) =>
            {
                fn(name);
            });
    }
}

const storageKey = "$$_debug_logging";
class CustomLoggerRegistry extends LoggerRegistry
{
    private readonly settings: Map<string, boolean>;
    #forceEnabled: boolean;

    constructor()
    {
        super();

        this.settings = this.#loadLoggerMap();
        this.saveSettings();
    }

    #loadLoggerMap(): Map<string, boolean>
    {
        const storedCache = JSON.parse(storage.getItem(storageKey)) as Array<[string, boolean]>;
        const loggerMap: Map<string, boolean> = new Map<string, boolean>(
            storedCache ?? []);

        const matches = debugSearchParam.test(window.location.search);
        if (!matches)
        {
            return loggerMap;
        }

        const searchParams = window.location.search
            .substring(1) // search query starts with ?
            .split("&")
            .map(x =>
            {
                const [param, value] = x.split("=");
                return [param, value];
            });

        // const [_, debugParam] = searchParams.find(([k, _v]: [string, string]) => "debug" === k);
        const debugParam = searchParams.find((pair) => /debug/i.test(pair[0]))[1];
        if (!debugParam)
        {
            return loggerMap;
        }

        if (/^false$/i.test(debugParam))
        {
            loggerMap.clear();
            return loggerMap;
        }

        if (/^true$/i.test(debugParam))
        {
            this.#forceEnabled = true;
        }

        const loggerNames = debugParam?.split(/[,;]/);
        return loggerNames.reduce(
            (map, option) =>
            {
                if (!option)
                {
                    return map;
                }

                const [_, name, enabled] = /^(\w+):?(true|false)?$/i.exec(option);

                map.set(name, enabled !== "false");
                return map;
            },
            loggerMap);
    }

    override enabled(loggerName: string): boolean
    {
        return this.#forceEnabled
            || (this.settings.has(loggerName)
                && this.settings.get(loggerName));
    }

    override enable(_loggers: Array<string>): this;
    override enable(_loggerName: string): this;
    override enable(opt: Array<string> | string): this
    {
        if (this.#forceEnabled)
        {
            return this;
        }

        const loggerNames = Array.isArray(opt) ? opt : [opt];
        loggerNames.forEach(loggerName =>
        {
            this.settings.set(loggerName, true);
        });

        this.saveSettings();
        return this;
    }

    override disable(_loggers: Array<string>): this;
    override disable(_loggerName: string): this;
    override disable(opt: Array<string> | string): this
    {
        if (this.#forceEnabled)
        {
            return this;
        }

        const loggerNames = Array.isArray(opt) ? opt : [opt];
        loggerNames.forEach(loggerName =>
        {
            this.settings.set(loggerName, false);
        });

        this.saveSettings();
        return this;
    }

    override toggle(_loggers: Array<string>): this;
    override toggle(_loggerName: string): this;
    override toggle(opt: Array<string> | string): this
    {
        if (this.#forceEnabled)
        {
            return this;
        }

        const loggerNames = Array.isArray(opt) ? opt : [opt];
        loggerNames.forEach(loggerName =>
        {
            const status = this.settings.get(loggerName);
            this.settings.set(loggerName, !status);
        });

        this.saveSettings();
        return this;
    }

    override set(loggerName: string, logger: CustomLogger): void
    {
        super.set(loggerName, logger);

        if (this.settings.get(loggerName) || logger.enabled)
        {
            this.settings.set(loggerName, logger.enabled);
            this.saveSettings();
        }
    }

    private saveSettings(): void
    {
        storage.setItem(
            storageKey,
            JSON.stringify(
                [...this.settings.entries()]
                    .map(([k, v]) =>
                    {
                        return [k, v];
                    })));
    }
}

let loggerRegistry = debugAllParam.exec(location.search)
    ? new LoggerRegistry()
    : new CustomLoggerRegistry();

if (window)
{
    window.tt_loggerRegistry = loggerRegistry;
}
export function getLoggerRegistry(): LoggerRegistry
{
    return loggerRegistry;
}
/**
 * Only intended for use in testing.
 * @param newRegistry
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export function __enableAllLoggersForTesting(): void
{
    loggerRegistry = new LoggerRegistry();
}

/**
 * Only intended for use in testing.
 * @param newRegistry
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export function __resetLoggersForTesting(): void
{
    loggerRegistry.forEach((name) =>
    {
        loggerRegistry.disable(name);
    });
}

export interface ICustomLogger extends ILogger
{
    withPrefix(prefixFn?: () => string): ICustomLogger;
}

/**
 * A custom implementation of ILogger that only emits messages if the loggerName is contained in the DEBUG setting.
 *
 * All messages will be prefixed with the `loggerName`
 */
class CustomLogger implements ICustomLogger
{
    private readonly _enabled: boolean;
    constructor(
        enabled: boolean,
        private readonly loggingContext: string,
        private readonly prefixFn?: () => string)
    {
        this._enabled = enabled;
    }

    private get prefix(): string
    {
        return this.prefixFn
            ? this.prefixFn()
            : this.loggingContext;
    }

    get enabled(): boolean { return this._enabled || loggerRegistry.enabled(this.loggingContext); }

    getLogLevel(): LogLevel { return Logger.getLogLevel(); }

    setLogLevel(_value: LogLevel): void { throw new Error(`${CustomLogger.name} does not support changing LogLevel.`); }

    withPrefix(prefixFn?: () => string): ICustomLogger
    {
        if (!prefixFn)
        {
            return this;
        }

        return new CustomLogger(this.enabled, this.loggingContext, prefixFn);
    }

    error(message: string | Error, ...args: Array<unknown>): void
    {
        if (message instanceof Error && args.length === 0)
        {
            console.error(message);
            return;
        }

        const msgStr = !(typeof message === "string")
            ? StringHelper.stringify(message, undefined, 4)
            : message;
        Logger.error(`${this.prefix} | ${msgStr}`, ...args);
    }

    warning(message: string | Error, ...args: Array<unknown>): void
    {
        const msgStr = !(typeof message === "string")
            ? StringHelper.stringify(message, undefined, 4)
            : message;
        Logger.warning(`${this.prefix} | ${msgStr}`, ...args);
    }

    information(message: string, ...args: Array<unknown>): void
    {
        if (!this.enabled)
        {
            return;
        }

        Logger.information(`${this.prefix} | ${message}`, ...args);
    }

    debug(message: string, ...args: Array<unknown>): void
    {
        if (!this.enabled)
        {
            return;
        }

        Logger.debug(`${this.prefix} | ${message}`, ...args);
    }

    trace(message: string, ...args: Array<unknown>): void
    {
        if (!this.enabled)
        {
            return;
        }

        Logger.trace(`${this.prefix} | ${message}`, ...args);
    }
}

/**
 * Creates a custom implementation of ILogger that only emits messages if the loggerName is contained in the DEBUG setting.
 *
 * All messages will be prefixed with the string returned by the `prefixFn`.
 *
 * WARNING and ERROR messages will always be emitted.Other log messages will be emitted if the Logger registry is
 * configured to enable the returned ILogger.
 *
 * Logger Registry can be configured through the DEBUG search query parameter. If the search query includes `DEBUG=true`
 * then all logging contexts will emit based on the default log level (`LogLevel.Information`), while `DEBUG=false` will
 * disable all Logging Contexts other than the default Context from emitting any messages.
 *
 * The DEBUG search query parameter can also contain a simple comma delimited list of Logging Contexts; i.e.,
 * `DEBUG=SomeComponent,SomeOtherComponent`.
 *
 * A more advanced use-case supported in the DEBUG search query parameter is a comma delimited list of colon delimited
 * key-value pairs, where each pair is made up of the Logging Context and a Log Level; i.e.,
 * `DEBUG=SomeComponent:DEBUG,SomeOtherComponent:ERROR`.
 *
 * The DEBUG search query parameter will be stored in the browser's Session storage, so that subsequent page reloads
 * will not require the DEBUG search query parameter to be present. The value in the DEBUG search query parameter will
 * always override any persisted settings.
 *
 * @param loggingContext
 * @param prefixFn function to provide a prefix for all logged messages
 * @param alwaysLog if `true` bypasses the logic looking at DEBUG setting
 */
export function getLoggerFor(loggingContext: string, prefixFn: () => string, alwaysLog?: boolean): ILogger;
/**
 * Creates a custom implementation of ILogger that only emits messages if the
 * loggerName is contained in the DEBUG environment variable.
 *
 * All messages will be prefixed with the `loggerContext`
 *
 * WARNING and ERROR messages will always be emitted.Other log messages will be emitted if the Logger registry is
 * configured to enable the returned ILogger.
 *
 * Logger Registry can be configured through the DEBUG search query parameter. If the search query includes `DEBUG=true`
 * then all logging contexts will emit based on the default log level (`LogLevel.Information`), while `DEBUG=false` will
 * disable all Logging Contexts other than the default Context from emitting any messages.
 *
 * The DEBUG search query parameter can also contain a simple comma delimited list of Logging Contexts; i.e.,
 * `DEBUG=SomeComponent,SomeOtherComponent`.
 *
 * A more advanced use-case supported in the DEBUG search query parameter is a comma delimited list of colon delimited
 * key-value pairs, where each pair is made up of the Logging Context and a Log Level; i.e.,
 * `DEBUG=SomeComponent:DEBUG,SomeOtherComponent:ERROR`.
 *
 * The DEBUG search query parameter will be stored in the browser's Session storage, so that subsequent page reloads
 * will not require the DEBUG search query parameter to be present. The value in the DEBUG search query parameter will
 * always override any persisted settings.
 *
 * @param loggingContext
 * @param alwaysLog if `true` bypasses the logic looking at DEBUG setting
 * @returns
 */
export function getLoggerFor(loggingContext: string, alwaysLog?: boolean): ICustomLogger;
/**
 * @private
 */
export function getLoggerFor(loggingContext: string, prefixFnOrAlwaysLog?: boolean | (() => string), alwaysLog?: boolean): ICustomLogger
{
    let prefixFn: () => string;
    if (typeof prefixFnOrAlwaysLog === "function")
    {
        prefixFn = prefixFnOrAlwaysLog;
    }
    else
    {
        alwaysLog = prefixFnOrAlwaysLog;
    }

    if (!loggerRegistry.has(loggingContext))
    {
        Logger.trace(`Creating custom logger for '${loggingContext}'`);

        const customLogger = new CustomLogger(alwaysLog, loggingContext);
        loggerRegistry.set(loggingContext, customLogger);
    }

    const customLogger = loggerRegistry.get(loggingContext)!;
    return prefixFn
        ? customLogger.withPrefix(prefixFn)
        : customLogger;
}
